import React, { useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { ensureCurrentUser, checkMarketplaceCurrentUser } from '../../util/data';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import classNames from 'classnames';
import { priceData } from '../../util/currency';
import { ensureListing } from '../../util/data';
import { getListingDisplayDate } from '../../util/listings';
import config from '../../config';
import { AvailabilityBadge } from '../../components';
import css from './ListingCard.css';
import { SuccessIcon, UpdateCircleIcon } from '../../icons';
import { getUserExternalReview } from '../../containers/ProfilePage/ProfilePage.duck';
import { useMatchingMatrix } from '../../hooks/useMatchingMatrix';
import {
    defaultRenderSizes,
    formatDate,
    handleListingCardClick,
    resolveInfoItemsData,
} from './helpers';
import FavoriteBadge from './FavoriteBadge';
import IdentitySection from './IdentitySection';
import {
    checkIfListingIsAnonymous,
    deanonymizeListing,
    shouldListingBeDeanonymizedForUser,
} from '../../util/anonym';
import InfoItem from './InfoItem';

const { userTypeRider, userTypeHorseowner } = config;

export const ListingCard = ({
    className,
    rootClassName,
    cardWrapperClassName,
    listingRefClassName,
    intl,
    listing,
    renderSizes = defaultRenderSizes,
    setActiveListing = () => null,
    postponed = {} /** postpone e.g. images loading */,
    noFavorite,
    favoriteComponent = null,
    currentUser,
    currentUserMainListing,
    assetsLoadingRequests,
    recommendationsNum,
    onGetUserExternalReview,
    identitySectionClassName,
    notifyOnScoreChange = () => null,
    handleListingCardClick: handleListingCardClickProp,
    listingAuthorId: listingAuthorIdProp,
    currentUserDefined,
    isRider,
    titleTag: titleTagProp,
    history,
}) => {
    const { assets: assetsPostponed, matching: matchingPostponed } = postponed;
    const cardRef = useRef(null);

    const classes = classNames(rootClassName || css.root, className);
    const ensuredListing = ensureListing(listing);

    const { attributes, relationships, author: ensuredAuthor } = ensuredListing;
    const { author: relationshipsAuthor } = relationships || {};
    const { price, publicData } = attributes;

    const {
        userRepresentationId,
        updatedAt,
        availability: user_availability,
        availabilityStatus: user_availabilityStatus,
    } = publicData;

    const displayDate = getListingDisplayDate(ensuredListing);
    const displayDateFormatted = displayDate && formatDate(new Date(displayDate));

    const isUserListing = !!userRepresentationId;

    const author = ensuredAuthor || relationshipsAuthor;
    const listingAuthorId = author?.data?.id?.uuid || listingAuthorIdProp;
    const currentUserId = currentUserDefined ? currentUser.id.uuid : null;
    const { userType: currentUserType, mainHorseId } =
        currentUser?.attributes?.profile?.publicData || {};
    const { representationListingId } = currentUser?.attributes?.profile?.protectedData || {};

    const isHorseOwner = currentUserType === userTypeHorseowner;

    const isSameUser =
        currentUserDefined && listingAuthorId && currentUserId === listingAuthorId
            ? `sameUser-${isUserListing ? 'rider' : 'horse'}`
            : null;

    const currentListing = shouldListingBeDeanonymizedForUser(ensuredListing, currentUser)
        ? deanonymizeListing(ensuredListing)
        : ensuredListing;

    const currentListingId = currentListing?.id?.uuid;
    const { formattedPrice, priceTitle } = isUserListing ? {} : priceData(price, intl);

    const syntheticUser = isUserListing
        ? {
              ...(author || {}),
              attributes: {
                  ...(author ? author.attributes : {}),
                  profile: {
                      ...(author && author.attributes ? author.attributes.profile : {}),
                      publicData: {
                          ...(author && author.attributes
                              ? author.attributes.profile.publicData
                              : {}),
                          availability: user_availability,
                          availabilityStatus: user_availabilityStatus,
                      },
                  },
              },
          }
        : null;

    /**
     * a horseowner looks on a horse listing;
     * not allowed, edge case is handled
     */
    const ownersView = isHorseOwner && !isUserListing;
    /**
     * a rider looks on a rider-listing;
     * not allowed, edge case is handled
     */
    const ridersView = isRider && isUserListing;
    const userViewsInvalid = Boolean(isSameUser || ridersView || ownersView);

    /**
     * no matching
     * - when rider searches for other riders
     * - when owner searches for other horses
     */
    const riderViewsRider = isRider && isUserListing;
    const matchingAllowed = Boolean(
        !matchingPostponed && !riderViewsRider && !ownersView && currentUserDefined
    );

    const riderMatchingParams = {
        riderListingId: matchingAllowed ? representationListingId : null,
        horseOwnerListingId: currentListingId,
    };

    const ownerMatchingParams = {
        riderListingId: matchingAllowed ? currentListingId : null,
        horseOwnerListingId: mainHorseId,
    };

    /* useEffect */
    const [matchingMatrixLoading, matchingScore] = useMatchingMatrix(
        isRider ? riderMatchingParams : ownerMatchingParams
    );

    useEffect(() => {
        if (isUserListing) {
            onGetUserExternalReview(userRepresentationId);
        }
    }, [isUserListing]);

    const { finalScore, scorePalette } = matchingScore || {};
    /**
     * the current user's own listing to match against, read from the profile:
     * the rider's representation listing, or the owner's main horse
     */
    const matchingScoreListingId = isRider ? representationListingId : mainHorseId;
    const matchingMatrixAllowed =
        !userViewsInvalid && currentUserDefined && currentListingId && matchingScoreListingId;

    useEffect(() => {
        scorePalette && notifyOnScoreChange(scorePalette);
    }, [scorePalette]);

    const { discipline, disciplineLevel, ridings } = resolveInfoItemsData({
        currentListing,
        currentUser,
        currentUserMainListing,
    });

    const infoItems = [discipline, ridings, disciplineLevel].filter(i => !!i && i.content);

    const infoLineMaybe = infoItems.length > 0;

    const additionalInfoSection = discipline &&
        discipline.content &&
        disciplineLevel &&
        disciplineLevel.content && (
            <div className={css.additionalInfoItem}>
                <FormattedMessage id="ListingCard.additionalInfo" />
            </div>
        );

    const isAnonymousListing = checkIfListingIsAnonymous(currentListing);

    return (
        <div className={classes} id={`ListingCard-${currentListingId}`}>
            {favoriteComponent ? (
                React.cloneElement(favoriteComponent, {
                    cardRef,
                })
            ) : (
                <FavoriteBadge
                    currentListing={currentListing}
                    noFavorite={noFavorite}
                    cardRef={cardRef}
                />
            )}

            <div
                className={classNames(css.cardWrapper, {
                    [cardWrapperClassName]: !!cardWrapperClassName,
                })}
                onClick={e =>
                    typeof handleListingCardClickProp === 'function'
                        ? handleListingCardClickProp(e)
                        : handleListingCardClick(e, {
                              history,
                              currentListing,
                          })
                }
                onMouseEnter={() => setActiveListing(currentListing.id)}
                onMouseLeave={() => setActiveListing(null)}
            >
                <main
                    className={classNames(css.listingRef, {
                        [listingRefClassName]: !!listingRefClassName,
                    })}
                    ref={cardRef}
                >
                    <IdentitySection
                        currentListing={currentListing}
                        renderSizes={renderSizes}
                        assetsPostponed={assetsPostponed}
                        assetsLoadingRequests={assetsLoadingRequests}
                        identitySectionClassName={identitySectionClassName}
                        recommendationsNum={recommendationsNum}
                        titleTagProp={titleTagProp}
                        isAnonymousListing={isAnonymousListing}
                    />

                    {currentUserId && matchingMatrixAllowed && (
                        <code
                            className={classNames({
                                [css.progressBar]: true,
                                [css[scorePalette]]: !!scorePalette,
                            })}
                        >
                            <mark style={{ width: `${finalScore || 0}%` }} />
                        </code>
                    )}

                    <section className={css.listingInfoSection}>
                        {matchingMatrixLoading && <code className={css.mmLoadingIndicator} />}
                        {!matchingMatrixLoading &&
                            typeof finalScore === 'number' &&
                            matchingMatrixAllowed && (
                                <p className={css.matchingScore}>
                                    {finalScore}% <FormattedMessage id="ListingCard.finalScore" />
                                </p>
                            )}
                        {infoLineMaybe && (
                            <>
                                <div className={css.infoLine}>
                                    {infoItems.map(({ content, matching }, index) => (
                                        <InfoItem
                                            key={index}
                                            listing={ensuredListing}
                                            isRider={isRider}
                                            content={content}
                                            matching={matching}
                                        />
                                    ))}
                                    {additionalInfoSection}
                                </div>
                                {!isUserListing && <div className={css.divider} />}
                            </>
                        )}
                        {!isUserListing && (
                            <aside className={css.horseListingSection}>
                                {displayDateFormatted && (
                                    <div className={css.listingCreationInfo}>
                                        {updatedAt ? (
                                            <UpdateCircleIcon />
                                        ) : (
                                            <SuccessIcon rootClassName={css.createdAtIcon} />
                                        )}
                                        {displayDateFormatted}
                                    </div>
                                )}
                                {formattedPrice && (
                                    <div className={css.price}>
                                        <div
                                            className={css.priceValue}
                                            title={priceTitle && priceTitle}
                                        >
                                            {formattedPrice}
                                        </div>
                                        <div className={css.perUnit}>
                                            <span>pro Monat</span>
                                        </div>
                                    </div>
                                )}
                            </aside>
                        )}
                    </section>
                    {isUserListing && syntheticUser && (
                        <div className={css.availabilityBadgeHolder}>
                            <AvailabilityBadge
                                user={syntheticUser}
                                editAllowed={false}
                                parentContainerRef={cardRef}
                                helpTextAllowed
                                isPublic
                            />
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

const mapStateToProps = (state, ownProps) => {
    const { listing } = ownProps;
    const { id, author, attributes } = listing || {};
    const { uuid: listingId } = id || { uuid: null };
    const {
        publicData: { userRepresentationId },
    } = attributes || { publicData: { userRepresentationId: null } };
    const currentUser = ensureCurrentUser(checkMarketplaceCurrentUser(state));
    const { Assets, ProfilePage, user } = state;
    const { currentUserMainListing } = user;
    const { assetsLoadingRequests } = Assets;
    const { externalReviewsRequests, externalReviewsData } = ProfilePage;

    const currentUserDefined = currentUser && currentUser.id;

    const isRider =
        currentUserDefined && currentUser.attributes.profile.publicData.userType === userTypeRider;

    const listingAuthorId = author && author.id && author.id.uuid;

    const externalReviews = externalReviewsData && externalReviewsData[listingAuthorId];

    const externalReviewsRequest =
        externalReviewsRequests && externalReviewsRequests[listingAuthorId];

    const externalReviewsDataAvailable =
        !externalReviewsRequest && Array.isArray(externalReviews) && externalReviews.length > 0
            ? externalReviews
            : null;

    const recommendationsApproved = externalReviewsDataAvailable
        ? externalReviewsDataAvailable.filter(({ status }) => status === 'approved').length
        : null;

    return {
        currentUser,
        currentUserMainListing,
        assetsLoadingRequests: Boolean(
            assetsLoadingRequests[listingId] || assetsLoadingRequests[userRepresentationId]
        ),
        externalReviews,
        currentUserDefined,
        listingAuthorId,
        isRider,
        recommendationsNum: recommendationsApproved,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onGetUserExternalReview: userReviewedId => dispatch(getUserExternalReview(userReviewedId)),
        dispatch,
    };
};

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withRouter,
    injectIntl
)(ListingCard);
